"use client";
import Button from "@/components/primitives/button";
import { usePickSlip } from "@/components/pick/create-pick";
import {
  SubscriptionPackage,
  getUserSubscriptions,
} from "@/utils/subscription";
import { BettorBasic, SubscriptionSchema } from "common";
import { useAuth } from "@clerk/clerk-react";
import { useState, useEffect } from "react";

export default function SubscriptionPackageCard({
  subscriptionPackage,
  capperHandle,
}: {
  subscriptionPackage: SubscriptionPackage;
  capperHandle: string;
}) {
  const slip = usePickSlip();
  const { getToken, isSignedIn } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);

  useEffect(() => {
    const handleSubscriptionSuccess = (event: CustomEvent) => {
      const { planId } = event.detail;
      if (planId && planId.toString() === subscriptionPackage.id.toString()) {
        console.log("Subscription success detected for package:", planId);
        setIsSubscribed(true);
      }
    };

    window.addEventListener(
      "subscriptionSuccess",
      handleSubscriptionSuccess as EventListener
    );

    return () => {
      window.removeEventListener(
        "subscriptionSuccess",
        handleSubscriptionSuccess as EventListener
      );
    };
  }, [subscriptionPackage.id]);

  useEffect(() => {
    console.log("checker");
    const checkSubscriptionStatus = async () => {
      if (!isSignedIn) {
        setIsSubscribed(false);
        return;
      }

      setLoadingSubscriptions(true);
      try {
        const token = await getToken();
        const userSubscriptions = await getUserSubscriptions(token);

        console.log("=== User Subscriptions Data ===");
        console.log("Raw subscription data from server:", userSubscriptions);
        console.log("Number of subscriptions:", userSubscriptions?.length || 0);
        console.log(
          "Current package ID we're checking:",
          subscriptionPackage.id
        );

        if (userSubscriptions) {
          // Log each subscription for debugging
          userSubscriptions.forEach((subscription: any, index: number) => {
            console.log(`Subscription ${index + 1}:`, {
              subscription_id: subscription.subscription_id,
              plan_id: subscription.plan?.id,
              plan_name: subscription.plan?.name,
              capper_handle: subscription.capper_handle,
              cancel_at_period_end: subscription.cancel_at_period_end,
            });
          });

          // Check if user has an active subscription to this plan
          // SubscriptionModel has { plan: { id, ... }, ... } structure
          const hasActiveSubscription = userSubscriptions.some(
            (subscription: any) =>
              subscription.plan?.id === subscriptionPackage.id &&
              !subscription.cancel_at_period_end // Active subscription should not be cancelled
          );

          console.log(
            "Has active subscription to this plan:",
            hasActiveSubscription
          );
          setIsSubscribed(hasActiveSubscription);
        } else {
          console.log("No subscription data received (null/undefined)");
        }
      } catch (error) {
        console.error("Error checking subscription status:", error);
      } finally {
        setLoadingSubscriptions(false);
      }
    };

    checkSubscriptionStatus();
  }, [subscriptionPackage.id, isSignedIn, getToken, slip.justSubscribed]);

  const handleSubscribeClick = async () => {
    if (isSubscribed) {
      return; // Don't allow clicking if already subscribed
    }

    // Use the new consistent approach with only IDs
    await slip.triggerSubscriptionCheckoutByIds(
      subscriptionPackage.id,
      capperHandle
    );
  };

  const packageName = subscriptionPackage.name;
  const packageDescription = subscriptionPackage.description;

  // Find the default price or the first active price
  const defaultPrice =
    subscriptionPackage.prices?.find(
      (price) =>
        price.id === (subscriptionPackage as any).default_price_id &&
        price.status === "active"
    ) ||
    subscriptionPackage.prices?.find((price) => price.status === "active") ||
    subscriptionPackage.prices?.[0];

  const packagePrice = defaultPrice
    ? (defaultPrice.amount_cents / 100).toFixed(2)
    : "0.00";
  const packageInterval = defaultPrice?.interval || "month";
  const pricePerDay = defaultPrice
    ? (
        defaultPrice.amount_cents /
        100 /
        (defaultPrice.interval === "week"
          ? 7
          : defaultPrice.interval === "month"
          ? 30
          : defaultPrice.interval === "year"
          ? 365
          : 1)
      ).toFixed(2)
    : "0.00";

  // Check if there are multiple pricing options
  const hasMultiplePrices =
    subscriptionPackage.prices && subscriptionPackage.prices.length > 1;
  const activePrices =
    subscriptionPackage.prices?.filter((price) => price.status === "active") ||
    [];

  return (
    <div className="relative flex flex-col bg-secondary-100 p-6 border rounded-xl w-full lg:max-w-[360px] h-full overflow-hidden text-white">
      <CardBackground />
      <div className="z-20 relative">
        <div className="flex-grow space-y-6 mb-4">
          <div>
            <h3 className="mb-2 text-headline-bold text-surface uppercase">
              {packageName}
            </h3>
          </div>
          <div>
            {/* <div className="mb-2 text-secondary-400 text-title-sm-medium">
            {hasMultiplePrices ? "Starting at" : "Price"}
          </div> */}
            <div className="flex flex-wrap justify-between items-end gap-1 mb-2">
              <div className="flex flex-col">
                <div className="text-display-lg text-secondary-700 whitespace-nowrap">
                  ${packagePrice}
                  <span className="text-secondary-400 text-title-lg-medium">
                    {" "}
                    / {packageInterval}
                  </span>
                </div>
                {/* {defaultPrice?.tier && (
                <div className="text-secondary-500 text-xs uppercase tracking-wide">
                  {defaultPrice.tier === "all_access"
                    ? "All Access"
                    : "Limited Access"}
                </div>
              )} */}
              </div>
              <div className="text-primary-200 text-title-medium uppercase whitespace-nowrap">
                Or as low as ${pricePerDay}/day
              </div>
            </div>

            {/* Show additional pricing options if available */}
            {/* {hasMultiplePrices && activePrices.length > 1 && (
            <div className="space-y-1 mt-3">
              <div className="text-label-sm text-secondary-400">
                Other options:
              </div>
              {activePrices.slice(0, 3).map(
                (price, index) =>
                  price.id !== defaultPrice?.id && (
                    <div
                      key={price.id}
                      className="flex justify-between items-center text-body-sm text-secondary-500"
                    >
                      <div className="flex flex-col">
                        <span>
                          ${(price.amount_cents / 100).toFixed(2)} /{" "}
                          {price.interval}
                        </span>
                        {price.tier && (
                          <span className="text-secondary-400 text-xs">
                            {price.tier === "all_access"
                              ? "All Access"
                              : "Limited Access"}
                          </span>
                        )}
                      </div>
                    </div>
                  )
              )}
              {activePrices.length > 3 && (
                <div className="text-body-xs text-secondary-400">
                  +{activePrices.length - 3} more options
                </div>
              )}
            </div>
          )} */}
          </div>

          <div className="space-y-4">
            <p className="text-body-regular text-secondary-700">
              {packageDescription}
            </p>
            <div className="space-y-2">
              <p className="mb-2 text-label-lg-bold text-surface uppercase">
                Post delivery
              </p>

              {/* discord logo, replace later */}
              <svg
                width="100"
                height="24"
                viewBox="0 0 100 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_17185_3210)">
                  <path
                    d="M19.8979 5.21648C18.4524 4.55473 16.9265 4.08526 15.3591 3.82007C15.1446 4.2035 14.9505 4.598 14.7777 5.00191C13.1081 4.75032 11.4102 4.75032 9.74054 5.00191C9.56761 4.59804 9.37354 4.20355 9.15914 3.82007C7.5907 4.0875 6.06378 4.55808 4.6169 5.21994C1.74448 9.46974 0.965808 13.614 1.35514 17.6994C3.03731 18.9423 4.92013 19.8875 6.92176 20.494C7.37247 19.8878 7.77129 19.2447 8.11399 18.5715C7.46308 18.3284 6.83484 18.0285 6.23653 17.6752C6.394 17.561 6.548 17.4433 6.69681 17.3291C8.43774 18.1478 10.3379 18.5723 12.2617 18.5723C14.1855 18.5723 16.0857 18.1478 17.8266 17.3291C17.9771 17.452 18.1311 17.5696 18.2869 17.6752C17.6874 18.029 17.058 18.3296 16.406 18.5733C16.7482 19.2461 17.1471 19.8887 17.5982 20.494C19.6015 19.8899 21.4858 18.9451 23.1683 17.7011C23.6251 12.9634 22.3879 8.85719 19.8979 5.21648ZM8.60715 15.1869C7.5222 15.1869 6.62587 14.2023 6.62587 12.9911C6.62587 11.7798 7.49106 10.7866 8.60369 10.7866C9.71632 10.7866 10.6057 11.7798 10.5867 12.9911C10.5677 14.2023 9.71286 15.1869 8.60715 15.1869ZM15.9163 15.1869C14.8296 15.1869 13.9367 14.2023 13.9367 12.9911C13.9367 11.7798 14.8019 10.7866 15.9163 10.7866C17.0306 10.7866 17.9131 11.7798 17.8941 12.9911C17.875 14.2023 17.022 15.1869 15.9163 15.1869Z"
                    fill="#5865F2"
                  />
                </g>
                <path
                  d="M32 7.21997H37.4915C38.8156 7.21997 39.9342 7.42739 40.8519 7.84033C41.7671 8.25309 42.4534 8.82894 42.9082 9.56613C43.3627 10.3033 43.592 11.1469 43.592 12.0973C43.592 13.0275 43.3546 13.8714 42.8798 14.6264C42.405 15.3834 41.6826 15.9815 40.7109 16.4228C39.7392 16.864 38.5357 17.0852 37.0991 17.0852H32.0004L32 7.21997ZM37.0426 14.578C37.934 14.578 38.6203 14.3545 39.0989 13.9093C39.5779 13.4621 39.8174 12.8543 39.8174 12.083C39.8174 11.3681 39.6039 10.7982 39.1775 10.3715C38.7511 9.94447 38.1051 9.72922 37.2418 9.72922H35.5235V14.5766L37.0426 14.578Z"
                  fill="white"
                />
                <path
                  d="M51.8005 17.0747C51.0399 16.8773 50.3536 16.5913 49.7441 16.2147V13.8748C50.2049 14.2312 50.8224 14.5251 51.5971 14.7567C52.3717 14.9862 53.1204 15.101 53.8445 15.101C54.1825 15.101 54.4382 15.0567 54.6111 14.9681C54.7843 14.8795 54.8709 14.7728 54.8709 14.65C54.8709 14.509 54.8246 14.3922 54.7299 14.2977C54.6353 14.2031 54.4522 14.1245 54.1808 14.0581L52.4906 13.6777C51.5227 13.4521 50.8365 13.1401 50.4283 12.7391C50.0198 12.3403 49.8164 11.8169 49.8164 11.1684C49.8164 10.6228 49.9935 10.1495 50.3518 9.74478C50.7078 9.34002 51.2148 9.02775 51.8727 8.80833C52.5309 8.58683 53.2992 8.47607 54.1825 8.47607C54.9712 8.47607 55.6936 8.56064 56.3518 8.72982C57.0096 8.89899 57.5528 9.11441 57.9855 9.37834V11.5913C57.5426 11.3275 57.0356 11.1201 56.4563 10.963C55.8787 10.8079 55.2854 10.7314 54.6735 10.7314C53.7901 10.7314 53.3497 10.8824 53.3497 11.1824C53.3497 11.3234 53.4181 11.4281 53.5548 11.4986C53.6916 11.5691 53.943 11.6416 54.3074 11.7181L55.7157 11.9718C56.6355 12.1309 57.3214 12.4106 57.772 12.8095C58.2229 13.2084 58.4481 13.7981 58.4481 14.5795C58.4481 15.4352 58.076 16.114 57.3294 16.6175C56.5829 17.1211 55.5245 17.3728 54.1524 17.3728C53.3455 17.3708 52.5611 17.272 51.8005 17.0747Z"
                  fill="white"
                />
                <path
                  d="M61.7976 16.7755C60.9911 16.3808 60.3813 15.8453 59.9748 15.1686C59.5684 14.4919 59.3633 13.7308 59.3633 12.8851C59.3633 12.0394 59.5744 11.282 59.9969 10.6157C60.4195 9.94949 61.0391 9.42545 61.8562 9.04503C62.6729 8.66461 63.6488 8.4751 64.7857 8.4751C66.1939 8.4751 67.3631 8.77112 68.2923 9.36321V11.9427C67.9645 11.7172 67.5819 11.5338 67.1453 11.3929C66.7087 11.252 66.242 11.1815 65.743 11.1815C64.8698 11.1815 64.1878 11.3406 63.6947 11.6607C63.2017 11.9807 62.9545 12.3979 62.9545 12.9153C62.9545 13.4226 63.1936 13.8375 63.6726 14.1636C64.1516 14.4878 64.8456 14.6508 65.757 14.6508C66.2258 14.6508 66.6887 14.5824 67.1453 14.4474C67.6001 14.3105 67.9925 14.1434 68.3204 13.946V16.441C67.2884 17.0613 66.0912 17.3712 64.7295 17.3712C63.5829 17.3672 62.6049 17.1698 61.798 16.7752L61.7976 16.7755Z"
                  fill="white"
                />
                <path
                  d="M71.79 16.7758C70.9772 16.3811 70.3575 15.8414 69.9308 15.1546C69.5044 14.4679 69.2891 13.7028 69.2891 12.8568C69.2891 12.0111 69.5023 11.2558 69.9308 10.5934C70.3593 9.93098 70.9751 9.41146 71.7841 9.03487C72.5906 8.65827 73.5546 8.47119 74.6711 8.47119C75.788 8.47119 76.7516 8.65848 77.5585 9.03487C78.3654 9.41146 78.9812 9.92681 79.4037 10.5854C79.8263 11.244 80.0374 11.999 80.0374 12.8551C80.0374 13.7008 79.8263 14.4661 79.4037 15.1526C78.9812 15.8393 78.3633 16.379 77.5504 16.7737C76.7376 17.1684 75.7778 17.3659 74.6694 17.3659C73.5605 17.3659 72.6008 17.1705 71.79 16.7758ZM76.0682 14.2749C76.4104 13.9326 76.5833 13.4796 76.5833 12.9156C76.5833 12.3519 76.4122 11.9026 76.0682 11.5705C75.7263 11.2363 75.2592 11.0691 74.668 11.0691C74.0662 11.0691 73.5953 11.2362 73.2513 11.5705C72.9094 11.9049 72.7382 12.3519 72.7382 12.9156C72.7382 13.4793 72.9094 13.9324 73.2513 14.2749C73.5935 14.6172 74.0662 14.7906 74.668 14.7906C75.2592 14.7885 75.7263 14.6174 76.0682 14.2749Z"
                  fill="white"
                />
                <path
                  d="M88.5171 8.99676V12.0415C88.159 11.8059 87.6961 11.6893 87.1228 11.6893C86.372 11.6893 85.7927 11.9169 85.3902 12.3719C84.9858 12.8271 84.7846 13.5357 84.7846 14.4944V17.0882H81.334V8.84341H84.7144V11.4653C84.9013 10.5066 85.2054 9.80003 85.6237 9.34276C86.0403 8.88757 86.5793 8.66016 87.2354 8.66016C87.7323 8.66016 88.159 8.77293 88.5171 8.99843V8.99676Z"
                  fill="white"
                />
                <path
                  d="M99.9843 6.93823V17.0886H96.5334V15.2421C96.2416 15.9369 95.7991 16.4665 95.2037 16.8288C94.6079 17.1894 93.8715 17.3706 92.9983 17.3706C92.2177 17.3706 91.5378 17.1813 90.956 16.8007C90.3746 16.4202 89.9261 15.8986 89.6102 15.2359C89.2963 14.5734 89.1371 13.8244 89.1371 12.9864C89.1273 12.1226 89.2942 11.3472 89.6382 10.6604C89.9805 9.9736 90.4651 9.43809 91.0889 9.0535C91.7128 8.6689 92.425 8.47556 93.2238 8.47556C94.8677 8.47556 95.9702 9.19051 96.5334 10.6183V6.93928L99.9843 6.93823ZM96.0183 14.2198C96.3703 13.8774 96.5453 13.4325 96.5453 12.8887C96.5453 12.3632 96.3742 11.9341 96.0323 11.6059C95.6901 11.2776 95.2212 11.1124 94.6317 11.1124C94.0503 11.1124 93.5857 11.2796 93.2375 11.6139C92.8893 11.9482 92.7164 12.381 92.7164 12.9168C92.7164 13.4523 92.8893 13.8894 93.2375 14.2278C93.5857 14.5661 94.0444 14.7351 94.6177 14.7351C95.1991 14.7331 95.6659 14.562 96.0183 14.2198Z"
                  fill="white"
                />
                <path
                  d="M46.5055 9.72005C47.4555 9.72005 48.2259 9.02771 48.2259 8.17333C48.2259 7.31894 47.4555 6.62695 46.5055 6.62695C45.5552 6.62695 44.7852 7.31929 44.7852 8.17333C44.7852 9.02736 45.5552 9.72005 46.5055 9.72005Z"
                  fill="white"
                />
                <path
                  d="M44.7891 10.7842C45.8432 11.2394 47.1512 11.2595 48.2295 10.7842V17.1303H44.7891V10.7842Z"
                  fill="white"
                />
                <defs>
                  <clipPath id="clip0_17185_3210">
                    <rect
                      width="22"
                      height="16.6739"
                      fill="white"
                      transform="translate(1.26172 3.82007)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 mt-auto">
          {/* {!isSubscribed && (
          <Button color="light" className="!justify-center w-full">
            Get a free post
          </Button>
        )} */}
          <Button
            className="!justify-center w-full"
            onClick={handleSubscribeClick}
            disabled={isSubscribed || loadingSubscriptions}
            color={isSubscribed ? "disabled" : undefined}
          >
            {loadingSubscriptions
              ? "Checking..."
              : isSubscribed
              ? "Subscribed"
              : "Subscribe"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const CardBackground = () => {
  return (
    <div
      className="z-10 absolute inset-0 bg-secondary-100"
      style={{
        maskImage: "linear-gradient(180deg, #D9D9D9CC 0%, #73737300 100%)",
        WebkitMaskImage:
          "linear-gradient(180deg, #D9D9D9CC 0%, #73737300 100%)",
      }}
    >
      <div
        className="z-10 absolute inset-0 opacity-90"
        style={{
          backgroundImage: "url(/images/grunge.jpg)",
          backgroundAttachment: "cover",
          mixBlendMode: "soft-light",
        }}
      ></div>
      <svg
        viewBox="0 0 457 1358"
        className="z-20 relative opacity-90 w-full h-auto"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g opacity="0.7">
          <path
            d="M445.016 1118.57L228.508 1335.08L12 1118.57"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 1017.96L228.508 1234.47L12 1017.96"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 917.353L228.508 1133.86L12 917.353"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 816.745L228.508 1033.25L12 816.745"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 716.138L228.508 932.646L12 716.138"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 615.53L228.508 832.038L12 615.53"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 514.922L228.508 731.43L12 514.922"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 414.315L228.508 630.823L12 414.315"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 313.708L228.508 530.215L12 313.708"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 213.1L228.508 429.608L12 213.1"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 112.493L228.508 329L12 112.493"
            stroke="#303030"
            strokeWidth="31.4039"
          />
          <path
            d="M445.016 11.8848L228.508 228.393L12 11.8848"
            stroke="#303030"
            strokeWidth="31.4039"
          />
        </g>
      </svg>
    </div>
  );
};
